import { useCallback } from 'react';

import {
  usePassengersAlternateIdentifiersCreateMutation,
  usePassengersAlternateIdentifiersDestroyMutation,
  usePassengersAlternateIdentifiersPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';

type UpsertAlternateIdentifierFormData = {
  alternateIdentifierId?: string;
  alternateIdentifierSource?: string;
  alternateIdentifier?: string;
  passengerId: string;
};

type UseUpsertAlternateIdentifierCallback = (
  data: UpsertAlternateIdentifierFormData
) => Promise<void>;

// Mirrors EMR_IDENTIFIER_SOURCE in apps/passengers/constants.py; the
// backend only treats identifiers with this source as searchable EMRs.
const DEFAULT_ALTERNATE_IDENTIFIER_SOURCE = 'EMR';

export function useManageAlternateIdentifier(): UseUpsertAlternateIdentifierCallback {
  const [createAlternateIdentifier] =
    usePassengersAlternateIdentifiersCreateMutation();
  const [updateAlternateIdentifier] =
    usePassengersAlternateIdentifiersPartialUpdateMutation();
  const [deleteAlternateIdentifier] =
    usePassengersAlternateIdentifiersDestroyMutation();

  return useCallback(
    async (data: UpsertAlternateIdentifierFormData) => {
      // No identifier, but we had a previous one so delete it
      if (!data.alternateIdentifier && data.alternateIdentifierId) {
        return await deleteAlternateIdentifier({
          id: data.alternateIdentifierId,
          passengerId: data.passengerId,
        }).unwrap();
      }

      // No identifier and no id, just return
      if (!data.alternateIdentifier) {
        return;
      }

      // Create new identifier record
      if (!data.alternateIdentifierId) {
        await createAlternateIdentifier({
          passengerId: data.passengerId,
          passengerAlternateIdentifier: {
            identifier: data.alternateIdentifier,
            identifier_source:
              data.alternateIdentifierSource ??
              DEFAULT_ALTERNATE_IDENTIFIER_SOURCE,
          },
        }).unwrap();
      } else {
        // Update existing identifier record
        await updateAlternateIdentifier({
          id: data.alternateIdentifierId,
          passengerId: data.passengerId,
          patchedPassengerAlternateIdentifier: {
            identifier: data.alternateIdentifier,
            // Future: support updating the source if we make this into a table
          },
        }).unwrap();
      }
    },
    [
      createAlternateIdentifier,
      updateAlternateIdentifier,
      deleteAlternateIdentifier,
    ]
  );
}
